import type { DrawRequestDTO, DrawResponseDTO, EntropyAudit, Orientation } from '@lilith-arcana-os/domain';

import {
  buildDrawnCards,
  EntropyFacade,
  EntropyNetworkError,
  resolveBootstrapSpread,
} from '@lilith-arcana-os/engines';
import { getCardById } from '@lilith-arcana-os/tarot-corpus/derived/cards-by-slug';

import { appEnv } from './env';

type EntropyWorkerRequest =
  | { type: 'shuffle'; deck: number[]; rounds?: number }
  | { type: 'orientations'; count: number }
  | {
      type: 'audit';
      drawnCardIds: number[];
      bakerRounds: number;
      context?: Record<string, unknown>;
    };

type EntropyWorkerResponse =
  | { type: 'shuffle'; deck: number[] }
  | { type: 'orientations'; values: Array<'upright' | 'reversed'> }
  | { type: 'audit'; audit: EntropyAudit }
  | { type: 'error'; message: string };

export type DrawResolution = 'hosted' | 'offline';
export type ResolvedDrawResponse = DrawResponseDTO & { resolvedBy: DrawResolution };

const DEFAULT_BAKER_ROUNDS = 3;

const createEntropyWorker = (): Worker =>
  new Worker(new URL('../workers/entropy.worker.ts', import.meta.url), {
    type: 'module',
  });

const requestWorker = (
  worker: Worker,
  payload: EntropyWorkerRequest,
): Promise<EntropyWorkerResponse> =>
  new Promise((resolve, reject) => {
    const handleMessage = (event: MessageEvent<EntropyWorkerResponse>) => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      resolve(event.data);
    };

    const handleError = (event: ErrorEvent) => {
      worker.removeEventListener('message', handleMessage);
      worker.removeEventListener('error', handleError);
      reject(new Error(event.message || 'Entropy worker failed.'));
    };

    worker.addEventListener('message', handleMessage, { once: true });
    worker.addEventListener('error', handleError, { once: true });
    worker.postMessage(payload);
  });

const runOfflineDrawInWorker = async (
  request: DrawRequestDTO,
  deck: number[],
): Promise<DrawResponseDTO> => {
  const spread = resolveBootstrapSpread(request.spreadId);

  if (deck.length < spread.cards) {
    throw new RangeError(`Deck must contain at least ${spread.cards} cards.`);
  }

  const worker = createEntropyWorker();

  try {
    const shuffledResponse = await requestWorker(worker, {
      type: 'shuffle',
      deck,
      rounds: DEFAULT_BAKER_ROUNDS,
    });

    if (shuffledResponse.type !== 'shuffle') {
      throw new Error(
        shuffledResponse.type === 'error'
          ? shuffledResponse.message
          : 'Entropy worker returned an unexpected shuffle payload.',
      );
    }

    const drawnCardIds = shuffledResponse.deck.slice(0, spread.cards);
    const orientationsResponse = await requestWorker(worker, {
      type: 'orientations',
      count: spread.cards,
    });

    if (orientationsResponse.type !== 'orientations') {
      throw new Error(
        orientationsResponse.type === 'error'
          ? orientationsResponse.message
          : 'Entropy worker returned an unexpected orientations payload.',
      );
    }

    const auditResponse = await requestWorker(worker, {
      type: 'audit',
      drawnCardIds,
      bakerRounds: DEFAULT_BAKER_ROUNDS,
      context: {
        question: request.question,
        spreadId: request.spreadId,
      },
    });

    if (auditResponse.type !== 'audit') {
      throw new Error(
        auditResponse.type === 'error'
          ? auditResponse.message
          : 'Entropy worker returned an unexpected audit payload.',
      );
    }

    return {
      cards: buildDrawnCards({
        cardIds: drawnCardIds,
        orientations: orientationsResponse.values as Orientation[],
        spreadId: request.spreadId,
      }),
      audit: auditResponse.audit,
    };
  } finally {
    worker.terminate();
  }
};

const hydrateDrawResponse = (
  response: DrawResponseDTO,
  resolvedBy: DrawResolution,
): ResolvedDrawResponse => ({
  ...response,
  cards: response.cards.map((card) => {
    const corpusCard = getCardById(card.id);

    if (!corpusCard) {
      return card;
    }

    return {
      ...corpusCard,
      orientation: card.orientation,
      position: card.position,
      draw_index: card.draw_index,
    };
  }),
  resolvedBy,
});

export async function performDrawAuto(params: {
  request: DrawRequestDTO;
  deck: number[];
  preferHosted: boolean;
  allowOfflineFallback: boolean;
}): Promise<ResolvedDrawResponse> {
  const facade = new EntropyFacade({
    preferServer: params.preferHosted,
    allowOfflineFallback: params.allowOfflineFallback,
    allowANUAugmentation: false,
    restBase: appEnv.restBase,
    nonce: appEnv.nonce,
  });

  if (params.preferHosted) {
    try {
      return hydrateDrawResponse(await facade.drawHosted(params.request), 'hosted');
    } catch (error) {
      if (!(error instanceof EntropyNetworkError) || !params.allowOfflineFallback) {
        throw error;
      }
    }
  }

  const offline =
    typeof Worker === 'undefined'
      ? await facade.drawOffline(params.request, params.deck)
      : await runOfflineDrawInWorker(params.request, params.deck).catch(() =>
          facade.drawOffline(params.request, params.deck),
        );

  return hydrateDrawResponse(offline, 'offline');
}
